// services/api/chatbot/chatbot-session.service.ts
import axios from "axios";

const PROJECT_KEY = process.env.EXPO_PUBLIC_X_BLOCKS_KEY as string;

/**
 * The conversational-agent service, on the app's OWN origin — `EXPO_PUBLIC_
 * CHATBOT_BASE_URL`, not `EXPO_PUBLIC_BLOCKS_BASE_URL` (the `blocksapi.*`
 * host). Per environment: prod `https://app.selise.club`, dev
 * `https://dtggna-dqrbx.slsblx.com` — the same host each environment's
 * `EXPO_PUBLIC_SHARE_URL` already names.
 *
 * Verified end to end 2026-08-09 against widget 421b12fa…, each candidate paired
 * with a bogus-sibling control on the same prefix:
 *
 *   GET  {base}/agents/v4/conversation/initiate?widget_id=&project_key=
 *          header x-blocks-key
 *     -> 200 {session_id, token, websocket_url, expires_at, is_success}
 *   POST {base}/agents/v4/chat/{session_id}?project_key=&pg=true
 *          headers x-blocks-token: <token from initiate>, x-blocks-key
 *     -> 200 text/event-stream, `event: chat_response`
 *        data: {"message":"Hello! How can I help you today?"}
 *   GET  {base}/agents/v4/BogusControlXyz   -> 404 {"detail":"Not Found"}  (control)
 *   POST {base}/agents/v4/BogusControlXyz   -> 405 Method Not Allowed      (control)
 *
 * Two earlier hosts were tried and are both wrong. `gpt.seliseblocks.com` does
 * not know this tenant at all — it answers `Application_Not_Found`,
 * indistinguishable from a garbage key. `agents.seliseblocks.com/api` serves the
 * same routes and the widget flow works there, but it is a different registrable
 * domain from the app origin, so `apiClient`'s session cookie is out of scope and
 * the KB calls in `wiki.services.ts` cannot authenticate. The app's own origin
 * shares a registrable domain with `EXPO_PUBLIC_BLOCKS_BASE_URL` in every
 * environment (selise.club / slsblx.com), so the cookie stays in scope there too
 * — see the constraint note in `wiki.services.ts`.
 */
export const AGENTS_API = `${process.env.EXPO_PUBLIC_CHATBOT_BASE_URL}/agents/v4`;

type SessionResponse = {
  session_id: string;
  token: string;
  expires_at: string;
  websocket_url?: string;
  /** Blocks reports business failures in the body, not the status. */
  is_success?: boolean;
};

/**
 * MUST be a GET. The same path answers `401 {"detail":"Token missing"}` to a
 * POST — that verb is the authenticated console's entry point, and the widget
 * flow has no bearer token to offer (`getToken()` returns null on web; the v4
 * session is an HttpOnly cookie on the app's own origin). Here `x-blocks-key`
 * plus the widget id IS the credential.
 */
export const initiateSession = async (widgetId: string) => {
  const res = await axios.get(`${AGENTS_API}/conversation/initiate`, {
    params: { widget_id: widgetId, project_key: PROJECT_KEY },
    headers: { "x-blocks-key": PROJECT_KEY },
  });
  const data = res.data as SessionResponse;

  // A rejected initiate can come back HTTP 200 with `is_success: false`, so
  // axios does not throw and the caller would open a chat with an empty session
  // id — surfacing as a mute widget rather than an error.
  if (data?.is_success === false || !data?.session_id) {
    throw new Error(
      "Chat session could not be initiated — check that the widget id is provisioned for this tenant.",
    );
  }

  return data;
};

export const sendChatMessage = async (
  sessionId: string,
  token: string,
  message: string,
  onChunk: (chunk: string) => void,
) => {
  const response = await fetch(
    `${AGENTS_API}/chat/${sessionId}?project_key=${PROJECT_KEY}&pg=true`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-blocks-token": token,
        "x-blocks-key": PROJECT_KEY,
      },
      body: JSON.stringify({ message }),
    },
  );

  const reader = response.body?.getReader();

  // React Native's fetch does not expose a readable stream body
  // (`response.body` is undefined), so `getReader()` is unavailable. In that
  // case read the full response text once and parse all SSE events — the reply
  // appears at once instead of token-by-token, but it actually displays.
  // Browsers keep the incremental streaming path.
  if (!reader) {
    const text = await response.text();
    parseSseEvents(text, onChunk);
    return;
  }

  const decoder = new TextDecoder();
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    parseSseEvents(decoder.decode(value), onChunk);
  }
};

// Parse SSE-formatted text (event:/data: line pairs) and emit each
// `chat_response` message via onChunk. Shared by the streaming (web) and
// full-text (React Native) paths.
const parseSseEvents = (text: string, onChunk: (chunk: string) => void) => {
  const lines = text.split("\n").filter((l) => l.trim());
  let currentEvent = "";
  for (const line of lines) {
    if (line.startsWith("event:")) {
      currentEvent = line.replace("event:", "").trim();
      continue;
    }
    if (line.startsWith("data:")) {
      try {
        const data = JSON.parse(line.replace("data:", "").trim());
        if (currentEvent === "chat_response" && data.message) {
          onChunk(data.message);
        }
      } catch {}
    }
  }
};
